import React, { useState, useEffect, useRef } from 'react';
import './DesktopCreateModal.css';

function DesktopCreateModal({
  isOpen,
  onClose,
  type, // 'list' or 'table'
  group, // the group to create for
  onSubmit // (type, prompt, group, columns?) => void
}) {
  const [prompt, setPrompt] = useState('');
  const [columns, setColumns] = useState(['Name', '']);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen) {
      setPrompt('');
      setColumns(['Name', '']);
      setIsSubmitting(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  // Handle click outside
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    // For tables, we need at least one column besides Name
    if (type === 'table') {
      const validColumns = columns.filter(c => c.trim());
      if (validColumns.length < 2) {
        alert('Please add at least one column besides Name');
        return;
      }
    } else if (!prompt.trim()) {
      return;
    }

    setIsSubmitting(true);
    try {
      if (type === 'table') {
        const validColumns = columns.filter(c => c.trim());
        await onSubmit(type, prompt.trim() || 'Custom table', group, validColumns);
      } else {
        await onSubmit(type, prompt.trim(), group);
      }
      onClose();
    } catch (error) {
      console.error('Create error:', error);
      setIsSubmitting(false);
    }
  };

  const handleAddColumn = () => {
    if (columns.length < 10) {
      setColumns([...columns, '']);
    }
  };

  const handleRemoveColumn = (index) => {
    if (index === 0) return; // Can't remove Name column
    setColumns(columns.filter((_, i) => i !== index));
  };

  const handleColumnChange = (index, value) => {
    const newColumns = [...columns];
    newColumns[index] = value;
    setColumns(newColumns);
  };

  const suggestions = type === 'list'
    ? [
        'Organize by giving capacity',
        'Group by engagement level',
        'Sort by last contact date',
        'Prioritize by relationship strength'
      ]
    : [
        'Last Contact',
        'Phone',
        'Email',
        'Notes',
        'Amount',
        'Status'
      ];

  if (!isOpen) return null;

  return (
    <div className="create-modal-backdrop" onClick={handleBackdropClick}>
      <div className="create-modal">
        {/* Header */}
        <div className="create-modal-header">
          <div className="create-modal-icon">
            {type === 'list' ? (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="8" y1="6" x2="21" y2="6"/>
                <line x1="8" y1="12" x2="21" y2="12"/>
                <line x1="8" y1="18" x2="21" y2="18"/>
                <circle cx="4" cy="6" r="1.5" fill="currentColor"/>
                <circle cx="4" cy="12" r="1.5" fill="currentColor"/>
                <circle cx="4" cy="18" r="1.5" fill="currentColor"/>
              </svg>
            ) : (
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="18" height="18" rx="3"/>
                <line x1="3" y1="9" x2="21" y2="9"/>
                <line x1="3" y1="15" x2="21" y2="15"/>
                <line x1="9" y1="3" x2="9" y2="21"/>
                <line x1="15" y1="3" x2="15" y2="21"/>
              </svg>
            )}
          </div>
          {type === 'list' ? (
            <form onSubmit={handleSubmit} style={{ flex: 1 }}>
              <input
                ref={inputRef}
                type="text"
                className="create-modal-input"
                placeholder={`How would you like to organize ${group?.name || 'this group'}?`}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={isSubmitting}
              />
            </form>
          ) : (
            <div className="create-modal-title">
              <h3>Create Table</h3>
              <span>for {group?.name || 'this group'}</span>
            </div>
          )}
          <button className="create-modal-close" onClick={onClose}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="create-modal-content">
          {isSubmitting ? (
            <div className="create-modal-loading">
              <div className="create-modal-loading-text">
                Creating your {type}...
              </div>
              <div className="create-modal-loading-bar">
                <div className="rainbow-loader" />
              </div>
            </div>
          ) : type === 'table' ? (
            <>
              <div className="create-modal-section-label">Columns</div>
              <div className="create-modal-columns">
                {columns.map((col, idx) => (
                  <div key={idx} className="column-input-row">
                    <input
                      ref={idx === 1 ? inputRef : null}
                      type="text"
                      className="column-input"
                      placeholder={idx === 0 ? 'Name (required)' : `Column ${idx + 1}`}
                      value={col}
                      onChange={(e) => handleColumnChange(idx, e.target.value)}
                      disabled={idx === 0 || isSubmitting}
                    />
                    {idx > 0 && (
                      <button
                        type="button"
                        className="column-remove"
                        onClick={() => handleRemoveColumn(idx)}
                        disabled={isSubmitting}
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                ))}
                {columns.length < 10 && (
                  <button
                    type="button"
                    className="column-add"
                    onClick={handleAddColumn}
                    disabled={isSubmitting}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14"/>
                    </svg>
                    Add Column
                  </button>
                )}
              </div>

              <div className="create-modal-section-label" style={{ marginTop: 20 }}>Quick Add</div>
              <div className="create-modal-suggestions">
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    className="create-modal-suggestion"
                    onClick={() => {
                      if (!columns.includes(suggestion)) {
                        setColumns([...columns.filter(c => c.trim()), suggestion, '']);
                      }
                    }}
                    disabled={isSubmitting || columns.includes(suggestion)}
                  >
                    + {suggestion}
                  </button>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="create-modal-section-label">Suggestions</div>
              <div className="create-modal-suggestions">
                {suggestions.map((suggestion, idx) => (
                  <button
                    key={idx}
                    className="create-modal-suggestion"
                    onClick={() => setPrompt(suggestion)}
                    disabled={isSubmitting}
                  >
                    <span className="suggestion-icon">✨</span>
                    {suggestion}
                  </button>
                ))}
              </div>
            </>
          )}

          {group && !isSubmitting && (
            <div className="create-modal-context">
              <span className="context-label">Creating for:</span>
              <span className="context-group">
                <span className="context-dot" style={{ background: group.color || '#a78bfa' }} />
                {group.name}
              </span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="create-modal-footer">
          <div className="create-modal-hint">
            {type === 'table'
              ? 'Add columns for your table. AI will fill in data from your contacts.'
              : 'Describe what you want and AI will create it'
            }
          </div>
          <button
            className="create-modal-submit"
            onClick={handleSubmit}
            disabled={(type === 'list' && !prompt.trim()) || isSubmitting}
          >
            {isSubmitting ? (
              <span className="submit-loading">Creating...</span>
            ) : (
              <>
                Create {type === 'list' ? 'List' : 'Table'}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DesktopCreateModal;
